import React, { useMemo } from 'react'

import { ButtonBase, useTheme } from '@material-ui/core'
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles'
import HomeIcon from '@material-ui/icons/Home'
import FolderIcon from '@material-ui/icons/Folder'

import { useRootInfrastructure } from '../../../hooks'
import { UnitRes, FolderRes } from '../../../store'
import { typographyStyle } from '../../../theme/typographyStyle'

interface Props {
    selectedUnitId: number | null
    setSelectedUnitId: (unitId: number) => void
    searchQuery: string
}

interface UnitDisplay {
    id: number
    name: string
    folderPath: string
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        sidebar: {
            width: 240,
            minWidth: 240,
            maxWidth: 240,
            borderRight: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            minHeight: 0,
            overflow: 'hidden',
        },
        header: {
            padding: theme.spacing(2.5, 3),
            borderBottom: `1px solid ${theme.palette.divider}`,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}08 0%, ${theme.palette.primary.main}03 100%)`,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1.5),
        },
        headerIcon: {
            width: 36,
            height: 36,
            borderRadius: theme.spacing(1),
            backgroundColor: theme.palette.primary.main,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
        headerTitle: {
            ...(typographyStyle(theme.typography.subtitle1) as Record<
                string,
                unknown
            >),
            fontWeight: 700,
            color: theme.palette.text.primary,
        },
        headerCount: {
            ...(typographyStyle(theme.typography.caption) as Record<
                string,
                unknown
            >),
            color: theme.palette.text.secondary,
            marginLeft: 'auto',
        },
        scrollContainer: {
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            padding: theme.spacing(1.5),
            '&::-webkit-scrollbar': {
                width: 6,
            },
            '&::-webkit-scrollbar-track': {
                backgroundColor: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
                backgroundColor: theme.palette.grey[300],
                borderRadius: 3,
                '&:hover': {
                    backgroundColor: theme.palette.grey[400],
                },
            },
        },
        unitButton: {
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
            padding: theme.spacing(1.25, 1.5),
            borderRadius: theme.spacing(1),
            marginBottom: theme.spacing(0.5),
            textAlign: 'left',
            transition: 'all 0.2s ease',
            border: `1px solid transparent`,
            '&:hover': {
                backgroundColor: theme.palette.grey[50],
                borderColor: theme.palette.grey[200],
                transform: 'translateX(2px)',
            },
        },
        unitButtonSelected: {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            boxShadow: `0 2px 8px ${theme.palette.primary.main}40`,
            '&:hover': {
                backgroundColor: theme.palette.primary.dark,
                borderColor: 'transparent',
                transform: 'translateX(2px)',
            },
        },
        unitIcon: {
            width: 28,
            height: 28,
            borderRadius: theme.spacing(0.75),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: theme.palette.grey[100],
            flexShrink: 0,
        },
        unitIconSelected: {
            backgroundColor: 'rgba(255,255,255,0.2)',
        },
        unitTextContainer: {
            flex: 1,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
        },
        unitName: {
            ...(typographyStyle(theme.typography.body2) as Record<
                string,
                unknown
            >),
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
        },
        folderPath: {
            fontSize: 11,
            color: theme.palette.text.secondary,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
        },
        folderPathSelected: {
            color: 'rgba(255,255,255,0.7)',
        },
        emptyState: {
            padding: theme.spacing(4),
            textAlign: 'center',
            color: theme.palette.text.secondary,
        },
    }),
)

export const InventoryUnitSideBar = ({
    selectedUnitId,
    setSelectedUnitId,
    searchQuery,
}: Props) => {
    const theme = useTheme()
    const classes = useStyles()

    const { unitList, folderList } = useRootInfrastructure(true)

    const folderMap = useMemo(() => {
        const map: Record<number, FolderRes> = {}
        folderList?.forEach((folder) => {
            map[folder.id] = folder
        })
        return map
    }, [folderList])

    const units = useMemo((): UnitDisplay[] => {
        if (!unitList) {
            return []
        }

        return unitList.map((unit: UnitRes) => {
            const folder = folderMap[unit.folder_id]
            const folderPath = folder ? `${folder.path}${folder.name}` : ''

            return {
                id: unit.id,
                name: unit.name,
                folderPath,
            }
        })
    }, [unitList, folderMap])

    const filteredUnits = useMemo(() => {
        if (!searchQuery) {
            return units
        }

        const query = searchQuery.toLowerCase()
        return units.filter(
            (unit) =>
                unit.name.toLowerCase().includes(query) ||
                unit.folderPath.toLowerCase().includes(query),
        )
    }, [units, searchQuery])

    if (!unitList || unitList.length === 0) {
        return (
            <aside className={classes.sidebar}>
                <div className={classes.header}>
                    <div className={classes.headerIcon}>
                        <HomeIcon
                            style={{
                                color: theme.palette.common.white,
                                fontSize: 20,
                            }}
                        />
                    </div>
                    <span className={classes.headerTitle}>Units</span>
                </div>
                <div className={classes.emptyState}>No units available</div>
            </aside>
        )
    }

    return (
        <aside className={classes.sidebar}>
            <div className={classes.header}>
                <div className={classes.headerIcon}>
                    <HomeIcon
                        style={{
                            color: theme.palette.common.white,
                            fontSize: 20,
                        }}
                    />
                </div>
                <span className={classes.headerTitle}>Units</span>
                <span className={classes.headerCount}>{units.length}</span>
            </div>

            <div className={classes.scrollContainer}>
                {filteredUnits.length === 0 ? (
                    <div className={classes.emptyState}>
                        No units match your search
                    </div>
                ) : (
                    filteredUnits.map((unit) => {
                        const isSelected = selectedUnitId === unit.id
                        return (
                            <ButtonBase
                                key={unit.id}
                                onClick={() => setSelectedUnitId(unit.id)}
                                className={`${classes.unitButton} ${
                                    isSelected ? classes.unitButtonSelected : ''
                                }`}
                            >
                                <div
                                    className={`${classes.unitIcon} ${
                                        isSelected
                                            ? classes.unitIconSelected
                                            : ''
                                    }`}
                                >
                                    <FolderIcon
                                        style={{
                                            fontSize: 16,
                                            color: isSelected
                                                ? theme.palette.common.white
                                                : theme.palette.primary.main,
                                        }}
                                    />
                                </div>
                                <div className={classes.unitTextContainer}>
                                    <span className={classes.unitName}>
                                        {unit.name}
                                    </span>
                                    {unit.folderPath && (
                                        <span
                                            className={`${classes.folderPath} ${
                                                isSelected
                                                    ? classes.folderPathSelected
                                                    : ''
                                            }`}
                                        >
                                            {unit.folderPath}
                                        </span>
                                    )}
                                </div>
                            </ButtonBase>
                        )
                    })
                )}
            </div>
        </aside>
    )
}
